import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export default function AdminLogin() {
  const navigate = useNavigate();
  const { login, register, isAuthenticated } = useAuth();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    username: '',
    password: ''
  });

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/admin/dashboard');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      if (isRegister) {
        await register(formData.email, formData.username, formData.password);
        toast.success('Registration successful!');
      } else {
        await login(formData.email, formData.password);
        toast.success('Login successful!');
      }
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="container mx-auto px-6 py-20 flex items-center justify-center min-h-[80vh]" data-testid="admin-login-page">
        <Card className="w-full max-w-md border-border bg-card">
          <CardHeader>
            <CardTitle className="text-2xl font-heading">{isRegister ? 'REGISTER' : 'ADMIN LOGIN'}</CardTitle>
            <CardDescription className="text-muted-foreground">
              {isRegister ? 'Create your admin account' : 'Access the admin dashboard'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="email" className="font-mono text-xs uppercase tracking-widest">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@primestatlab.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  data-testid="email-input"
                  className="bg-surface border-border"
                />
              </div>
              {isRegister && (
                <div>
                  <Label htmlFor="username" className="font-mono text-xs uppercase tracking-widest">Username</Label>
                  <Input
                    id="username"
                    type="text"
                    placeholder="John Doe"
                    value={formData.username}
                    onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                    required
                    data-testid="username-input"
                    className="bg-surface border-border"
                  />
                </div>
              )}
              <div>
                <Label htmlFor="password" className="font-mono text-xs uppercase tracking-widest">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="••••••••"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required
                  data-testid="password-input"
                  className="bg-surface border-border"
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-primary text-white hover:bg-primary/90 font-bold uppercase tracking-wider py-6"
                disabled={loading}
                data-testid="submit-button"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin mr-2" size={16} />
                    {isRegister ? 'Registering...' : 'Logging in...'}
                  </>
                ) : (
                  <>{isRegister ? 'Register' : 'Login'}</>
                )}
              </Button>
            </form>
            <div className="mt-4 text-center">
              <button
                onClick={() => setIsRegister(!isRegister)}
                className="text-sm text-muted-foreground hover:text-primary transition-colors"
                data-testid="toggle-form"
              >
                {isRegister ? 'Already have an account? Login' : "Don't have an account? Register"}
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}